import { useCallback, useState } from 'react';

import DAIToken from '../ContractABI/DAIToken.json';
import {
  REACT_APP_ETH_RPC,
  REACT_APP_OPENZK_RPC,
  REACT_APP_OZK_TOKEN,
  REACT_APP_OZUSD_TOKEN,
} from '../Config/Config';
import { useAccount } from 'wagmi';
import BigNumber from 'bignumber.js';
import Web3, { ContractAbi } from 'web3';

export function useBalance() {
  const { isConnected } = useAccount();

  const DEFAULT_L1_BALANCE = [
    { symbol: 'ETH', balance: '0' },
    { symbol: 'USDC', balance: '0' },
    { symbol: 'USDT', balance: '0' },
    { symbol: 'DAI', balance: '0' },
  ];
  const DEFAULT_L2_BALANCE = [
    { symbol: 'ozETH', balance: '0' },
    { symbol: 'ozUSD', balance: '0' },
  ];

  const [ozEthL1Balance, setOzEthL1Balance] = useState('0');
  const [ozUsdL1Balance, setOzUsdL1Balance] = useState('0');

  const [l1TokenBalances, setL1TokenBalances] =
    useState<{ symbol: string; balance: string }[]>(DEFAULT_L1_BALANCE);

  const [l2TokenBalances, setL2TokenBalances] =
    useState<{ symbol: string; balance: string }[]>(DEFAULT_L2_BALANCE);

  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });

  const formatterEth = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 6,
  });

  const loadL1Balance = useCallback(
    async (
      address: string,
      tokens: {
        contract: string;
        symbol: string;
        abi: ContractAbi;
        decimals: number;
      }[],
    ) => {
      if (!isConnected) return;
      const web3 = new Web3(REACT_APP_ETH_RPC);
      const tokenBalances = await Promise.all(
        tokens.map(async (token) => {
          const decimals = token?.decimals ?? 18;
          const normalizedAbi = Array.isArray(token?.abi)
            ? (token.abi as unknown[]).flat(2)
            : [];

          if (!token?.contract) {
            return { symbol: token?.symbol ?? '', balance: '0' };
          }
          if (token.symbol === 'ETH') {
            const ethBalance = await web3.eth.getBalance(address);
            return {
              symbol: token.symbol,
              balance: formatterEth.format(
                BigNumber(ethBalance.toString())
                  .div(Math.pow(10, decimals))
                  .toNumber(),
              ),
            };
          }

          try {
            // Validate contract address format
            if (!web3.utils.isAddress(token.contract)) {
              console.warn(`Invalid contract address for ${token.symbol}: ${token.contract}`);
              return {
                symbol: token.symbol,
                balance: '0',
              };
            }

            // Check if contract has code at the address
            const code = await web3.eth.getCode(token.contract);
            if (!code || code === '0x' || code === '0x0') {
              console.warn(`No contract code found at address for ${token.symbol}: ${token.contract}`);
              return {
                symbol: token.symbol,
                balance: '0',
              };
            }

            // Validate ABI
            if (!normalizedAbi || normalizedAbi.length === 0) {
              console.warn(`Invalid or empty ABI for ${token.symbol}`);
              return {
                symbol: token.symbol,
                balance: '0',
              };
            }

            const contract = new web3.eth.Contract(
              normalizedAbi as ContractAbi,
              token.contract,
            );
            if (!contract.methods?.balanceOf) {
              throw new Error('balanceOf not available in ABI');
            }
            const balance = (await contract.methods
              .balanceOf(address)
              .call()) as string;

            return {
              symbol: token.symbol,
              balance: formatter.format(
                BigNumber(balance).div(Math.pow(10, decimals)).toNumber(),
              ),
            };
          } catch (error) {
            // Only log non-validation errors to avoid noise
            const errorMessage = error instanceof Error ? error.message : String(error);
            if (!errorMessage.includes('Invalid') && !errorMessage.includes('No contract')) {
              console.error(`Error fetching L1 balance for ${token.symbol}:`, error);
            }
            return { symbol: token.symbol, balance: '0' };
          }
        }),
      );

      setL1TokenBalances(tokenBalances.filter(Boolean));
    },
    [isConnected],
  );

  const loadL2Balance = useCallback(
    async (
      address: string,
      tokens: {
        contract: string;
        symbol: string;
        abi: ContractAbi;
        decimals: number;
      }[],
    ) => {
      if (!isConnected) return;

      const web3 = new Web3(REACT_APP_OPENZK_RPC);

      const tokenBalances = await Promise.all(
        tokens.map(async (token) => {
          const decimals = token?.decimals ?? 18;
          const normalizedAbi = Array.isArray(token?.abi)
            ? (token.abi as unknown[]).flat(2)
            : [];
          if (!token?.contract) {
            return { symbol: token?.symbol ?? '', balance: '0' };
          }
          if (token.symbol === 'ozETH') {
            const ethBalance = await web3.eth.getBalance(address);

            return {
              symbol: token.symbol,
              balance: formatterEth.format(
                BigNumber(ethBalance.toString())
                  .div(Math.pow(10, decimals))
                  .toNumber(),
              ),
            };
          }

          try {
            // Validate contract address format
            if (!web3.utils.isAddress(token.contract)) {
              console.warn(`Invalid contract address for ${token.symbol}: ${token.contract}`);
              return {
                symbol: token.symbol,
                balance: '0',
              };
            }

            // Check if contract has code at the address
            const code = await web3.eth.getCode(token.contract);
            if (!code || code === '0x' || code === '0x0') {
              console.warn(`No contract code found at address for ${token.symbol}: ${token.contract}`);
              return {
                symbol: token.symbol,
                balance: '0',
              };
            }

            // Validate ABI
            if (!normalizedAbi || normalizedAbi.length === 0) {
              console.warn(`Invalid or empty ABI for ${token.symbol}`);
              return {
                symbol: token.symbol,
                balance: '0',
              };
            }

            const contract = new web3.eth.Contract(
              normalizedAbi as ContractAbi,
              token.contract,
            );
            if (!contract.methods?.balanceOf) {
              throw new Error('balanceOf not available in ABI');
            }
            const balance = (await contract.methods
              .balanceOf(address)
              .call()) as string;

            return {
              symbol: token.symbol,
              balance: formatter.format(
                BigNumber(balance).div(Math.pow(10, decimals)).toNumber(),
              ),
            };
          } catch (error) {
            // Only log non-validation errors to avoid noise
            const errorMessage = error instanceof Error ? error.message : String(error);
            if (!errorMessage.includes('Invalid') && !errorMessage.includes('No contract')) {
              console.error(`Error fetching L2 balance for ${token.symbol}:`, error);
            }
            return {
              symbol: token.symbol,
              balance: '0',
            };
          }
        }),
      );
      setL2TokenBalances(tokenBalances.filter(Boolean));
    },
    [isConnected],
  );

  const loadWithdrawBalance = useCallback(
    async (address: string) => {
      if (!address || !isConnected) return;

      const web3 = new Web3(REACT_APP_ETH_RPC);

      const ozUsdContract = new web3.eth.Contract(
        DAIToken,
        REACT_APP_OZUSD_TOKEN,
      );
      const ethContract = new web3.eth.Contract(DAIToken, REACT_APP_OZK_TOKEN);

      const [ozUsdResult, ozEthL1Result] = await Promise.all([
        (await ozUsdContract.methods.balanceOf(address).call()) as string,
        (await ethContract.methods.balanceOf(address).call()) as string,
      ]);

      setOzEthL1Balance(
        BigNumber(ozEthL1Result.toString()).div(Math.pow(10, 18)).toString(),
      );
      setOzUsdL1Balance(
        BigNumber(ozUsdResult.toString()).div(Math.pow(10, 18)).toString(),
      );
    },
    [isConnected],
  );

  const resetL1Balance = useCallback(() => {
    setL1TokenBalances(DEFAULT_L1_BALANCE);
  }, []);

  const resetL2Balance = useCallback(() => {
    setL2TokenBalances(DEFAULT_L2_BALANCE);
  }, []);

  const resetWithdrawBalance = useCallback(() => {
    setOzEthL1Balance('0');
    setOzUsdL1Balance('0');
  }, []);

  return {
    loadL1Balance,
    loadL2Balance,
    resetL1Balance,
    resetL2Balance,
    loadWithdrawBalance,
    resetWithdrawBalance,
    ozUsdL1Balance,
    ozEthL1Balance,
    l1TokenBalances,
    l2TokenBalances,
  };
}
